import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Plus, Save, Trash2, Eye, ChevronDown, ChevronRight, ArrowLeft, BookOpen, RefreshCw, Sparkles, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLessonEditor() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const theme = useTheme();
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [curriculumBooks, setCurriculumBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [loadingBooks, setLoadingBooks] = useState(true);
  // AI Curriculum Builder state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProposal, setAiProposal] = useState(null);
  const [aiApproving, setAiApproving] = useState(false);
  const [allBooks, setAllBooks] = useState([]);
  // Batch AI Content state
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  // Fetch available curriculum books
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/admin/books`, { headers: headers(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const books = data.books || [];
          setCurriculumBooks(books);
          // Auto-select first book or restore from localStorage
          const savedId = localStorage.getItem('adminLessonBookId');
          const saved = books.find(b => b.book_id === savedId);
          if (saved) {
            setSelectedBookId(saved.book_id);
          } else if (books.length > 0) {
            setSelectedBookId(books[0].book_id);
          }
        }
      } catch (err) {
        toast.error('Failed to load curriculum books');
      } finally {
        setLoadingBooks(false);
      }
    })();
  }, [headers]);

  const fetchLessons = useCallback(async () => {
    if (!selectedBookId) return;
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/all?book_id=${selectedBookId}`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLessons(data.lessons || []);
      }
    } catch (err) {
      toast.error('Failed to load lessons');
    }
  }, [headers, selectedBookId]);

  useEffect(() => {
    if (selectedBookId) {
      fetchLessons();
      setSelectedLesson(null);
      setEditing(null);
    }
  }, [selectedBookId, fetchLessons]);

  const handleBookChange = (bookId) => {
    setSelectedBookId(bookId);
    localStorage.setItem('adminLessonBookId', bookId);
    setAiProposal(null);
  };

  // Fetch ALL published books (for AI builder — books that have chapters but may not have lessons yet)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/books`);
        if (res.ok) {
          const data = await res.json();
          setAllBooks(data.books || []);
        }
      } catch { /* silent */ }
    })();
  }, []);

  // AI Curriculum Builder
  const generateAICurriculum = async (bookId) => {
    setAiGenerating(true);
    setAiProposal(null);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/ai-curriculum/${bookId}`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('AI generation started — Claude is analyzing the book...');
        // Start polling for the result
        pollForProposal(bookId, data.proposal_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'AI generation failed');
        setAiGenerating(false);
      }
    } catch (err) {
      toast.error('AI curriculum generation failed');
      setAiGenerating(false);
    }
  };

  const pollForProposal = (bookId, proposalId) => {
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/admin/ai-curriculum/${bookId}/status/${proposalId}`, {
          headers: headers(), credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'ready') {
            setAiProposal(data);
            setAiGenerating(false);
            toast.success(`AI proposed ${data.lesson_count} lessons!`);
            return;
          } else if (data.status === 'failed') {
            toast.error(data.error || 'AI generation failed');
            setAiGenerating(false);
            return;
          }
          // Still generating — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch {
        setAiGenerating(false);
        toast.error('Lost connection while waiting for AI');
      }
    };
    setTimeout(poll, 5000);
  };

  const approveProposal = async () => {
    if (!aiProposal || !selectedBookForAI) return;
    setAiApproving(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/ai-curriculum/${selectedBookForAI}/approve`, {
        method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ proposal_id: aiProposal.proposal_id }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created} lessons!`);
        setAiProposal(null);
        // Refresh book list and lessons
        const bRes = await fetch(`${API_URL}/api/lessons/admin/books`, { headers: headers(), credentials: 'include' });
        if (bRes.ok) {
          const bData = await bRes.json();
          setCurriculumBooks(bData.books || []);
        }
        // Switch to the newly created book
        handleBookChange(selectedBookForAI);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Approval failed');
      }
    } catch {
      toast.error('Approval request failed');
    } finally {
      setAiApproving(false);
    }
  };

  // Track which book is being processed by AI
  const [selectedBookForAI, setSelectedBookForAI] = useState(null);

  const startAIBuilder = (bookId) => {
    setSelectedBookForAI(bookId);
    generateAICurriculum(bookId);
  };

  // Batch AI Content Generator
  const startBatchGeneration = async () => {
    if (!selectedBookId) return;
    const draftsWithoutContent = lessons.filter(l => !l.content_status || l.content_status !== 'ready');
    if (draftsWithoutContent.length === 0) {
      toast.info('All lessons already have AI content');
      return;
    }
    if (!window.confirm(`Generate AI content for ${draftsWithoutContent.length} lessons? This will take about ${Math.ceil(draftsWithoutContent.length * 0.7)} minutes.`)) return;

    setBatchRunning(true);
    setBatchProgress(null);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/ai-content-batch/${selectedBookId}`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'nothing_to_do') {
          toast.info(data.message);
          setBatchRunning(false);
          return;
        }
        toast.success(`Batch started — generating content for ${data.total} lessons`);
        setBatchProgress({ ...data, completed: 0, failed: 0, current: null });
        pollBatchProgress(selectedBookId, data.batch_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Batch start failed');
        setBatchRunning(false);
      }
    } catch {
      toast.error('Batch generation failed');
      setBatchRunning(false);
    }
  };

  const pollBatchProgress = (bookId, batchId) => {
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/admin/ai-content-batch/${bookId}/status/${batchId}`, {
          headers: headers(), credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setBatchProgress(data);
          if (data.status === 'complete') {
            setBatchRunning(false);
            toast.success(`Batch complete! ${data.completed} lessons written, ${data.failed} failed.`);
            fetchLessons();
            return;
          }
          // Refresh lesson list periodically to show sidebar updates
          if (data.completed > 0 && data.completed % 2 === 0) fetchLessons();
          setTimeout(poll, 5000);
        } else {
          setTimeout(poll, 8000);
        }
      } catch {
        setTimeout(poll, 8000);
      }
    };
    setTimeout(poll, 6000);
  };

  const selectLesson = async (moduleKey) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons/${moduleKey}`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSelectedLesson(moduleKey);
        setEditing(data);
      }
    } catch (err) {
      toast.error('Failed to load lesson');
    }
  };

  const saveLesson = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/${editing.module_key}`, {
        method: 'PUT',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(editing),
      });
      if (res.ok) {
        toast.success('Lesson saved');
        fetchLessons();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
      }
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (moduleKey) => {
    if (!window.confirm(`Delete lesson ${moduleKey}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/${moduleKey}`, {
        method: 'DELETE',
        headers: headers(),
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Lesson deleted');
        if (selectedLesson === moduleKey) {
          setSelectedLesson(null);
          setEditing(null);
        }
        fetchLessons();
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const regenerateSummary = async () => {
    if (!editing?.module_key) return;
    setRegenerating(true);
    try {
      // First save current content so AI has latest material
      await fetch(`${API_URL}/api/lessons/admin/${editing.module_key}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify(editing),
      });
      // Clear existing summary so the batch endpoint picks this lesson
      await fetch(`${API_URL}/api/lessons/admin/${editing.module_key}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ ...editing, grade_school_summary: null }),
      });
      // Generate
      const res = await fetch(`${API_URL}/api/lessons/admin/generate-summaries?batch_size=1&offset=0`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.generated > 0) {
          // Refresh the lesson
          const r2 = await fetch(`${API_URL}/api/lessons/${editing.module_key}`, { headers: headers(), credentials: 'include' });
          if (r2.ok) {
            const updated = await r2.json();
            setEditing(updated);
            toast.success('Summary regenerated');
          }
        } else {
          toast.error('Summary generation failed');
        }
      }
    } catch {
      toast.error('Failed to regenerate summary');
    } finally {
      setRegenerating(false);
    }
  };

  // AI Lesson Content Generator
  const [generatingContent, setGeneratingContent] = useState(false);

  const generateLessonContent = async () => {
    if (!editing?.module_key) return;
    setGeneratingContent(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/ai-content/${editing.module_key}`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        toast.success('AI is writing lesson content — polling for result...');
        pollForContent(editing.module_key);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'AI content generation failed');
        setGeneratingContent(false);
      }
    } catch {
      toast.error('AI content generation failed');
      setGeneratingContent(false);
    }
  };

  const pollForContent = (moduleKey) => {
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/${moduleKey}`, { headers: headers(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.content_status === 'ready') {
            setEditing(data);
            setGeneratingContent(false);
            toast.success('Lesson content generated! Review and save when ready.');
            return;
          } else if (data.content_status === 'failed') {
            toast.error(data.content_error || 'AI generation failed');
            setGeneratingContent(false);
            return;
          }
        }
        setTimeout(poll, 4000);
      } catch {
        setGeneratingContent(false);
      }
    };
    setTimeout(poll, 5000);
  };

  const createLesson = async () => {
    if (!selectedBookId) { toast.error('Select a book first'); return; }
    const prefix = curriculumBooks.find(b => b.book_id === selectedBookId)?.title?.includes('Voice') ? 'VOT_' : '';
    const key = prompt(`Enter module key (e.g., ${prefix}INTERIOR_29):`);
    if (!key) return;
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/create`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify({
          module_key: key.toUpperCase(),
          book_id: selectedBookId,
          title: `New Lesson: ${key}`,
          lesson_type: 'interior',
          lesson_num: lessons.length + 1,
          status: 'draft',
          opening_paragraphs: ['Enter your lesson content here.'],
        }),
      });
      if (res.ok) {
        toast.success('Lesson created');
        fetchLessons();
        selectLesson(key.toUpperCase());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Create failed');
      }
    } catch (err) {
      toast.error('Create failed');
    }
  };

  const seedForBook = async (reset = false) => {
    if (!selectedBookId) return;
    const action = reset ? 'reset-curriculum' : 'seed-curriculum';
    if (reset && !window.confirm('This will DELETE and RESEED all lessons for this book. Are you sure?')) return;
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons/admin/${action}/${selectedBookId}`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.seeded || 0} lessons ${reset ? 'reset & reseeded' : 'seeded'}`);
        fetchLessons();
        // Refresh book list
        const bRes = await fetch(`${API_URL}/api/lessons/admin/books`, { headers: headers(), credentials: 'include' });
        if (bRes.ok) setCurriculumBooks((await bRes.json()).books || []);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Seed failed');
      }
    } catch (err) {
      toast.error('Seed request failed');
    } finally {
      setSeeding(false);
    }
  };

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const updateField = (path, value) => {
    setEditing(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const statusColors = { published: theme.successColor, draft: theme.textMuted };

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, fontFamily: "'Times New Roman', Georgia, serif" }} data-testid="admin-lesson-editor">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-sm" style={{ color: theme.navText }} data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Admin Dashboard
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Lesson Content Manager</span>
        <div className="flex gap-2">
          <button onClick={() => navigate('/admin/team')}
            className="px-3 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 border transition-all"
            style={{ background: 'rgba(217,119,6,0.08)', color: '#d97706', borderColor: 'rgba(217,119,6,0.25)' }}
            data-testid="btn-team-management">
            <Eye className="w-3.5 h-3.5" /> Team
          </button>
          <button onClick={startBatchGeneration} disabled={batchRunning || !selectedBookId || lessons.length === 0}
            className="px-3 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 border transition-all"
            style={{ background: batchRunning ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.12)', color: '#8b5cf6', borderColor: 'rgba(139,92,246,0.25)' }}
            data-testid="btn-batch-ai-content">
            <Sparkles className="w-3.5 h-3.5" /> {batchRunning ? 'Running...' : 'AI Write All Drafts'}
          </button>
          <button onClick={() => seedForBook(false)} disabled={seeding || !selectedBookId}
            className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: theme.accentBorder, color: theme.accentPrimary }}
            data-testid="btn-seed-curriculum">
            {seeding ? 'Seeding...' : 'Seed Curriculum'}
          </button>
          <button onClick={() => seedForBook(true)} disabled={seeding || !selectedBookId}
            className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#ef4444' }}
            data-testid="btn-reset-curriculum">
            <RefreshCw className="w-3 h-3 inline mr-1" />Reset & Reseed
          </button>
          <button onClick={createLesson}
            className="px-3 py-1.5 rounded text-xs border flex items-center gap-1" style={{ borderColor: theme.accentBorder, color: theme.accentPrimary }}
            data-testid="btn-create-lesson">
            <Plus className="w-3 h-3" /> New Lesson
          </button>
        </div>
      </nav>

      {/* Book Selector Bar */}
      <div className="px-6 py-3 border-b flex items-center gap-4" style={{ borderColor: theme.borderPrimary, background: theme.bgSecondary }}>
        <BookOpen className="w-4 h-4" style={{ color: theme.accentPrimary }} />
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Book:</span>
        {loadingBooks ? (
          <span className="text-xs" style={{ color: theme.textMuted }}>Loading books...</span>
        ) : curriculumBooks.length === 0 ? (
          <span className="text-xs" style={{ color: theme.textMuted }}>No books with curricula found. Seed a curriculum first.</span>
        ) : (
          <div className="flex gap-2 flex-wrap" data-testid="book-selector">
            {curriculumBooks.map(book => (
              <button key={book.book_id} onClick={() => handleBookChange(book.book_id)}
                className="px-4 py-1.5 rounded-full text-xs font-bold tracking-wider border transition-all"
                style={{
                  background: selectedBookId === book.book_id ? theme.accentPrimary : 'transparent',
                  color: selectedBookId === book.book_id ? '#fff' : theme.textPrimary,
                  borderColor: selectedBookId === book.book_id ? theme.accentPrimary : theme.borderPrimary,
                }}
                data-testid={`book-tab-${book.book_id.slice(0,8)}`}>
                {book.title?.replace(/:.*/, '') || 'Untitled'}
                <span className="ml-2 opacity-60">({book.lesson_count})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Batch Progress Banner */}
      {batchRunning && batchProgress && (
        <div className="px-6 py-3 border-b flex items-center gap-4" style={{ borderColor: 'rgba(139,92,246,0.2)', background: 'rgba(139,92,246,0.04)' }} data-testid="batch-progress-banner">
          <Sparkles className="w-4 h-4 animate-pulse flex-shrink-0" style={{ color: '#8b5cf6' }} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold" style={{ color: '#8b5cf6' }}>
                Claude is writing lessons — {batchProgress.completed} of {batchProgress.total} complete
                {batchProgress.failed > 0 && <span className="text-red-400 ml-2">({batchProgress.failed} failed)</span>}
              </span>
              {batchProgress.current && (
                <span className="text-[10px] font-mono" style={{ color: theme.textMuted }}>
                  Currently writing: {batchProgress.current}
                </span>
              )}
            </div>
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(139,92,246,0.15)' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{
                background: '#8b5cf6',
                width: `${batchProgress.total > 0 ? ((batchProgress.completed + batchProgress.failed) / batchProgress.total) * 100 : 0}%`
              }} />
            </div>
          </div>
        </div>
      )}

      <div className="flex h-[calc(100vh-105px)]">
        {/* Sidebar — lesson list */}
        <div className="w-72 border-r overflow-y-auto flex-shrink-0" style={{ borderColor: theme.borderPrimary, background: theme.bgSecondary }}>
          <div className="p-3">
            <p className="text-[10px] tracking-widest uppercase mb-3" style={{ color: theme.textMuted }}>
              {lessons.length} LESSONS
            </p>
            {lessons.map(l => (
              <button key={l.module_key} onClick={() => selectLesson(l.module_key)}
                className="w-full text-left px-3 py-2.5 rounded-lg mb-1 border transition-all"
                style={{
                  background: selectedLesson === l.module_key ? theme.accentBg : 'transparent',
                  borderColor: selectedLesson === l.module_key ? theme.accentBorder : 'transparent',
                }}
                data-testid={`lesson-item-${l.module_key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold truncate" style={{ color: theme.textPrimary }}>{l.title || l.module_key}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: statusColors[l.status] || theme.textMuted }}>
                    {l.status?.toUpperCase()}
                  </span>
                </div>
                <p className="text-[10px] mt-0.5" style={{ color: theme.textMuted }}>{l.module_key}</p>
              </button>
            ))}
            {lessons.length === 0 && (
              <div className="text-center py-6 px-2">
                <p className="text-sm mb-4" style={{ color: theme.textMuted }}>
                  No lessons yet for this book.
                </p>
                <button onClick={() => seedForBook(false)} disabled={seeding}
                  className="w-full px-3 py-2 rounded text-xs border mb-2" style={{ borderColor: theme.accentBorder, color: theme.accentPrimary }}
                  data-testid="sidebar-seed-btn">
                  {seeding ? 'Seeding...' : 'Seed from Template'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Editor panel */}
        <div className="flex-1 overflow-y-auto">
          {!editing ? (
            <div className="flex flex-col items-center justify-center h-full p-8" data-testid="no-lesson-selected">
              {aiProposal ? (
                /* AI Proposal Review */
                <div className="w-full max-w-3xl" data-testid="ai-proposal-review">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: theme.accentPrimary }}>
                        AI-Generated Curriculum Proposal
                      </h2>
                      <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                        {aiProposal.book_title} — {aiProposal.lesson_count} lessons proposed (prefix: {aiProposal.prefix})
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={approveProposal} disabled={aiApproving}
                        className="px-4 py-2 rounded text-xs font-bold flex items-center gap-1.5 transition-all"
                        style={{ background: '#16a34a', color: '#fff' }}
                        data-testid="btn-approve-proposal">
                        <Check className="w-3.5 h-3.5" /> {aiApproving ? 'Creating...' : 'Approve & Create Lessons'}
                      </button>
                      <button onClick={() => setAiProposal(null)}
                        className="px-3 py-2 rounded text-xs border flex items-center gap-1"
                        style={{ borderColor: theme.borderPrimary, color: theme.textMuted }}
                        data-testid="btn-dismiss-proposal">
                        <X className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 rounded-xl border p-4" style={{ borderColor: theme.borderPrimary, background: theme.bgCard }}>
                    {(aiProposal.lessons || []).map((les, idx) => {
                      const typeColors = { legend: '#16a34a', corner: '#2563eb', border: '#d97706', warning: '#dc2626', landmark: '#7c3aed', interior: '#d4a54a' };
                      return (
                        <div key={idx} className="flex items-start gap-3 py-2 border-b last:border-0" style={{ borderColor: theme.borderPrimary + '40' }}>
                          <span className="text-[10px] font-mono w-6 text-right pt-0.5" style={{ color: theme.textMuted }}>{idx + 1}</span>
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded w-16 text-center"
                            style={{ background: (typeColors[les.lesson_type] || '#888') + '20', color: typeColors[les.lesson_type] || '#888' }}>
                            {les.lesson_type}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-bold" style={{ color: theme.textPrimary }}>{les.title}</p>
                            <p className="text-[10px] font-mono" style={{ color: theme.textMuted }}>{les.module_key}</p>
                            {les.summary && <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>{les.summary}</p>}
                            {les.scripture_ref && <p className="text-[10px] italic mt-0.5" style={{ color: theme.accentPrimary }}>{les.scripture_ref}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : aiGenerating ? (
                /* AI Generating */
                <div className="text-center" data-testid="ai-generating">
                  <Sparkles className="w-10 h-10 mx-auto mb-4 animate-pulse" style={{ color: theme.accentPrimary }} />
                  <p className="text-sm font-bold mb-1" style={{ color: theme.textPrimary }}>Claude is analyzing the book...</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Designing a Treasure Map curriculum structure. This may take 30-60 seconds.</p>
                </div>
              ) : (
                /* Default: Select lesson or AI Builder */
                <div className="text-center max-w-lg">
                  <p className="mb-6" style={{ color: theme.textMuted }}>Select a lesson from the sidebar to edit</p>
                  {/* AI Curriculum Builder for books without lessons */}
                  {(() => {
                    const booksWithLessons = new Set(curriculumBooks.map(b => b.book_id));
                    const booksWithoutCurriculum = allBooks.filter(b => !booksWithLessons.has(b.book_id) && b.chapter_count > 0);
                    if (booksWithoutCurriculum.length === 0) return null;
                    return (
                      <div className="mt-4 rounded-xl border p-5" style={{ borderColor: theme.accentBorder, background: theme.accentBg }}
                        data-testid="ai-builder-panel">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4" style={{ color: theme.accentPrimary }} />
                          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: theme.accentPrimary }}>
                            AI Curriculum Builder
                          </span>
                        </div>
                        <p className="text-xs mb-4" style={{ color: theme.textSecondary }}>
                          These books have chapters uploaded but no curriculum yet. Claude can analyze the content and propose a full Treasure Map lesson structure.
                        </p>
                        <div className="space-y-2">
                          {booksWithoutCurriculum.map(book => (
                            <div key={book.book_id} className="flex items-center justify-between p-3 rounded-lg border"
                              style={{ borderColor: theme.borderPrimary, background: theme.bgPrimary }}>
                              <div>
                                <p className="text-sm font-bold" style={{ color: theme.textPrimary }}>{book.title?.replace(/:.*/, '')}</p>
                                <p className="text-[10px]" style={{ color: theme.textMuted }}>{book.chapter_count} chapters available</p>
                              </div>
                              <button onClick={() => startAIBuilder(book.book_id)}
                                className="px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all"
                                style={{ background: theme.accentPrimary, color: '#fff' }}
                                data-testid={`btn-ai-build-${book.book_id.slice(0,8)}`}>
                                <Sparkles className="w-3 h-3" /> Generate Curriculum
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto p-6 space-y-6">
              {/* Header actions */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: theme.accentPrimary }}>{editing.title}</h2>
                <div className="flex gap-2">
                  <button onClick={generateLessonContent} disabled={generatingContent}
                    className="px-3 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 transition-all"
                    style={{ background: generatingContent ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.15)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.25)' }}
                    data-testid="btn-ai-generate-content">
                    <Sparkles className="w-3.5 h-3.5" /> {generatingContent ? 'Generating...' : 'AI Write Content'}
                  </button>
                  <button onClick={() => navigate(`/lesson/${editing.module_key}`)}
                    className="px-3 py-1.5 rounded text-xs border flex items-center gap-1"
                    style={{ borderColor: theme.borderPrimary, color: theme.textSecondary }}
                    data-testid="btn-preview-lesson">
                    <Eye className="w-3 h-3" /> Preview
                  </button>
                  <button onClick={saveLesson} disabled={saving}
                    className="px-4 py-1.5 rounded text-xs font-bold flex items-center gap-1"
                    style={{ background: theme.accentPrimary, color: '#fff' }}
                    data-testid="btn-save-lesson">
                    <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => deleteLesson(editing.module_key)}
                    className="px-3 py-1.5 rounded text-xs border flex items-center gap-1"
                    style={{ borderColor: `${theme.labelColor}40`, color: theme.labelColor }}
                    data-testid="btn-delete-lesson">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
              {generatingContent && (
                <div className="flex items-center gap-3 p-3 rounded-lg border mt-2" style={{ borderColor: 'rgba(139,92,246,0.25)', background: 'rgba(139,92,246,0.05)' }} data-testid="ai-content-generating-banner">
                  <Sparkles className="w-4 h-4 animate-pulse" style={{ color: '#8b5cf6' }} />
                  <p className="text-xs" style={{ color: '#8b5cf6' }}>Claude is writing this lesson — opening paragraphs, GIT panel, Three Voices, discussion questions... This may take 30-60 seconds.</p>
                </div>
              )}

              {/* Basic Info */}
              <EditorSection title="Basic Info" sectionKey="basic" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <FieldRow label="Title" theme={theme}>
                  <input value={editing.title || ''} onChange={e => updateField('title', e.target.value)}
                    className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }}
                    data-testid="input-title" />
                </FieldRow>
                <div className="grid grid-cols-3 gap-3">
                  <FieldRow label="Module Key" theme={theme}>
                    <input value={editing.module_key || ''} disabled
                      className="w-full px-3 py-2 rounded border text-sm opacity-50" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                  <FieldRow label="Type" theme={theme}>
                    <select value={editing.lesson_type || 'interior'} onChange={e => updateField('lesson_type', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }}>
                      <option value="interior">Interior</option>
                      <option value="landmark">Landmark</option>
                      <option value="warning">Warning</option>
                      <option value="corner">Corner</option>
                      <option value="border">Border</option>
                    </select>
                  </FieldRow>
                  <FieldRow label="Status" theme={theme}>
                    <select value={editing.status || 'draft'} onChange={e => updateField('status', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }}
                      data-testid="select-status">
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </FieldRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Next Route" theme={theme}>
                    <input value={editing.next_route || ''} onChange={e => updateField('next_route', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                  <FieldRow label="Next Label" theme={theme}>
                    <input value={editing.next_label || ''} onChange={e => updateField('next_label', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                </div>
              </EditorSection>

              {/* Opening Content */}
              <EditorSection title="Opening Content" sectionKey="opening" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <FieldRow label="Scripture Reference" theme={theme}>
                  <input value={editing.opening_scripture_ref || ''} onChange={e => updateField('opening_scripture_ref', e.target.value)}
                    className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }}
                    placeholder="e.g., Revelation 6:1-17" />
                </FieldRow>
                <FieldRow label="Opening Paragraphs" theme={theme}>
                  <ArrayEditor items={editing.opening_paragraphs || []}
                    onChange={items => updateField('opening_paragraphs', items)}
                    theme={theme} placeholder="Enter paragraph text..." multiline />
                </FieldRow>
                <FieldRow label="Grade School Summary (AI-Generated)" theme={theme}>
                  <textarea value={editing.grade_school_summary || ''} onChange={e => updateField('grade_school_summary', e.target.value)}
                    rows={3} className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }}
                    placeholder="A simplified summary for younger readers..." data-testid="input-grade-school-summary" />
                  <button onClick={regenerateSummary} disabled={regenerating}
                    className="mt-2 px-3 py-1.5 rounded text-xs font-bold tracking-wider transition-all"
                    style={{ background: regenerating ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.15)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.25)' }}
                    data-testid="btn-regenerate-summary">
                    {regenerating ? 'Generating...' : 'Regenerate with AI'}
                  </button>
                </FieldRow>
              </EditorSection>

              {/* GIT Panel */}
              <EditorSection title="GIT Panel" sectionKey="git" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <FieldRow label="Verse Text" theme={theme}>
                  <textarea value={editing.git_panel?.verse || ''} onChange={e => updateField('git_panel.verse', e.target.value)}
                    className="w-full px-3 py-2 rounded border text-sm" rows={4} style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Verse Reference" theme={theme}>
                    <input value={editing.git_panel?.verse_ref || ''} onChange={e => updateField('git_panel.verse_ref', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                  <FieldRow label="Anchor Phrase" theme={theme}>
                    <input value={editing.git_panel?.anchor_phrase || ''} onChange={e => updateField('git_panel.anchor_phrase', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                </div>
                {['WHO?', 'WHAT?', 'WHEN?'].map((q, i) => (
                  <FieldRow key={q} label={`GIT: ${q}`} theme={theme}>
                    <textarea value={editing.git_panel?.answers?.[i]?.a || ''} rows={3}
                      onChange={e => {
                        const answers = [...(editing.git_panel?.answers || [{q:'WHO?',a:''},{q:'WHAT?',a:''},{q:'WHEN?',a:''}])];
                        answers[i] = { q, a: e.target.value };
                        updateField('git_panel.answers', answers);
                      }}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                ))}
              </EditorSection>

              {/* Three Voices */}
              <EditorSection title="Three Voices" sectionKey="voices" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                {['teacher', 'professor', 'pastoral'].map(voice => (
                  <FieldRow key={voice} label={voice.charAt(0).toUpperCase() + voice.slice(1)} theme={theme}>
                    <textarea value={editing.three_voices?.[voice] || ''} rows={6}
                      onChange={e => updateField(`three_voices.${voice}`, e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                ))}
              </EditorSection>

              {/* Rules Out */}
              <EditorSection title="What This Rules Out" sectionKey="rules" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <textarea value={editing.rules_out || ''} rows={4}
                  onChange={e => updateField('rules_out', e.target.value)}
                  className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
              </EditorSection>

              {/* Book Reading Hook */}
              <EditorSection title="Book Reading Hook" sectionKey="hook" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Chapter Number" theme={theme}>
                    <input type="number" value={editing.book_hook?.chapter_num || ''} onChange={e => updateField('book_hook.chapter_num', parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                  <FieldRow label="Chapter Title" theme={theme}>
                    <input value={editing.book_hook?.chapter_title || ''} onChange={e => updateField('book_hook.chapter_title', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                </div>
                <FieldRow label="Preview Text" theme={theme}>
                  <textarea value={editing.book_hook?.preview_text || ''} rows={2} onChange={e => updateField('book_hook.preview_text', e.target.value)}
                    className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Appendix Chapter #" theme={theme}>
                    <input type="number" value={editing.book_hook?.appendix_chapter_num || ''} onChange={e => updateField('book_hook.appendix_chapter_num', parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                  <FieldRow label="Appendix Title" theme={theme}>
                    <input value={editing.book_hook?.appendix_title || ''} onChange={e => updateField('book_hook.appendix_title', e.target.value)}
                      className="w-full px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
                  </FieldRow>
                </div>
              </EditorSection>

              {/* Discussion Questions */}
              <EditorSection title="Discussion Questions" sectionKey="questions" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <ArrayEditor items={editing.discussion_questions || []}
                  onChange={items => updateField('discussion_questions', items)}
                  theme={theme} placeholder="Enter a discussion question..." />
              </EditorSection>

              {/* Dig Deeper */}
              <EditorSection title="Dig Deeper Links" sectionKey="dig" expanded={expandedSections} toggle={toggleSection} theme={theme}>
                <DigDeeperEditor items={editing.dig_deeper || []}
                  onChange={items => updateField('dig_deeper', items)}
                  theme={theme} />
              </EditorSection>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function EditorSection({ title, sectionKey, expanded, toggle, theme, children }) {
  const isOpen = expanded[sectionKey] !== false; // default open
  return (
    <div className="rounded-xl border" style={{ borderColor: theme.borderPrimary, background: theme.bgCard }}>
      <button onClick={() => toggle(sectionKey)} className="w-full flex items-center justify-between px-5 py-3" data-testid={`section-${sectionKey}`}>
        <span className="text-sm font-bold" style={{ color: theme.accentPrimary }}>{title}</span>
        {isOpen ? <ChevronDown className="w-4 h-4" style={{ color: theme.textMuted }} /> : <ChevronRight className="w-4 h-4" style={{ color: theme.textMuted }} />}
      </button>
      {isOpen && <div className="px-5 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function FieldRow({ label, theme, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-wider uppercase block mb-1" style={{ color: theme.textMuted }}>{label}</label>
      {children}
    </div>
  );
}

function ArrayEditor({ items, onChange, theme, placeholder, multiline }) {
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => { const next = [...items]; next[i] = val; onChange(next); };

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          {multiline ? (
            <textarea value={item} onChange={e => update(i, e.target.value)} rows={3} placeholder={placeholder}
              className="flex-1 px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
          ) : (
            <input value={item} onChange={e => update(i, e.target.value)} placeholder={placeholder}
              className="flex-1 px-3 py-2 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
          )}
          <button onClick={() => remove(i)} className="px-2 rounded" style={{ color: theme.labelColor }}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button onClick={add} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: theme.accentPrimary }}>
        <Plus className="w-3 h-3" /> Add Item
      </button>
    </div>
  );
}

function DigDeeperEditor({ items, onChange, theme }) {
  const add = () => onChange([...items, { code: '', title: '', book_id: '', chapter_num: null }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 items-end">
          <div>
            <label className="text-[9px] uppercase" style={{ color: theme.textMuted }}>Code</label>
            <input value={item.code || ''} onChange={e => update(i, 'code', e.target.value)} placeholder="APP-X"
              className="w-full px-2 py-1.5 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
          </div>
          <div className="col-span-2">
            <label className="text-[9px] uppercase" style={{ color: theme.textMuted }}>Title</label>
            <input value={item.title || ''} onChange={e => update(i, 'title', e.target.value)}
              className="w-full px-2 py-1.5 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
          </div>
          <div className="flex gap-1 items-end">
            <div className="flex-1">
              <label className="text-[9px] uppercase" style={{ color: theme.textMuted }}>Ch#</label>
              <input type="number" value={item.chapter_num || ''} onChange={e => update(i, 'chapter_num', parseInt(e.target.value) || null)}
                className="w-full px-2 py-1.5 rounded border text-sm" style={{ background: theme.bgInput, borderColor: theme.borderPrimary, color: theme.textPrimary }} />
            </div>
            <button onClick={() => remove(i)} className="px-1.5 py-1.5" style={{ color: theme.labelColor }}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      <button onClick={add} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: theme.accentPrimary }}>
        <Plus className="w-3 h-3" /> Add Link
      </button>
    </div>
  );
}
